<template>
  <div class="dropdown color-modes" ref="dropdown">
    <button
      class="btn btn-link nav-link py-2 px-0 px-lg-2 dropdown-toggle"
      id="bd-theme"
      type="button"
      :aria-expanded="dropdownOpen.toString()"
      @click="toggleDropdown"
    >
      <span :title="`Tema: ${currentTheme} ${isAuto ? `(sistema: ${systemScheme})` : ''}`">
        <i :class="activeIconClass" aria-hidden="true"></i>
      </span>
      <span class="d-none ms-2" id="bd-theme-text">{{ currentTheme }}</span>
    </button>

    <ul
      class="dropdown-menu fs-6"
      aria-labelledby="bd-theme-text"
      style="min-width: 155px"
      :class="{ show: dropdownOpen }"
    >
      <li v-for="option in themeOptions" :key="option.value">
        <button
          type="button"
          class="dropdown-item align-items-center d-flex"
          :class="{ active: currentTheme === option.value }"
          @click="setAndShowTheme(option.value)"
        >
          <i :class="option.iconClass" aria-hidden="true"></i>
          {{ option.label }}
          <span
            v-if="option.value === 'auto'"
            class="ms-auto badge text-bg-secondary fw-normal"
          >
            {{ systemScheme }}
          </span>
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';

const themeOptions = [
  { value: 'light', label: 'Light', iconClass: 'fa-solid fa-cloud-sun me-2 theme-icon' },
  { value: 'dark',  label: 'Dark',  iconClass: 'fa-solid fa-cloud-moon me-2 theme-icon' },
  { value: 'auto',  label: 'Auto',  iconClass: 'fa-solid fa-circle-half-stroke me-2 theme-icon' },
];

const currentTheme  = ref('');
const dropdownOpen  = ref(false);
const dropdown      = ref(null);
const mediaQuery    = window.matchMedia('(prefers-color-scheme: dark)');
const systemScheme  = ref(mediaQuery.matches ? 'dark' : 'light');
const isAuto        = computed(() => currentTheme.value === 'auto');

const activeIconClass = computed(() => {
  const effective = isAuto.value ? systemScheme.value : currentTheme.value;
  return themeOptions.find(o => o.value === effective)?.iconClass ?? '';
});

// ── Tema ─────────────────────────────────────────────────────────────────────
const getStoredTheme  = () => localStorage.getItem('theme');
const setStoredTheme  = (theme) => localStorage.setItem('theme', theme);

const applyTheme = (theme) => {
  document.documentElement.setAttribute(
    'data-bs-theme',
    theme === 'auto' ? systemScheme.value : theme
  );
};

const showActiveTheme = (theme) => {
  currentTheme.value = theme;
  setStoredTheme(theme);
  applyTheme(theme);
};

// ── Dropdown ──────────────────────────────────────────────────────────────────
const toggleDropdown  = () => { dropdownOpen.value = !dropdownOpen.value; };
const closeDropdown   = () => { dropdownOpen.value = false; };
const setAndShowTheme = (theme) => { showActiveTheme(theme); closeDropdown(); };

const handleClickOutside = (e) => {
  if (dropdown.value && !dropdown.value.contains(e.target)) closeDropdown();
};

// ── Cambio del sistema en tiempo real ─────────────────────────────────────────
const onSystemChange = (e) => {
  const newScheme = e.matches ? 'dark' : 'light';
  systemScheme.value = newScheme;

  if (isAuto.value) {
    document.documentElement.setAttribute('data-bs-theme', newScheme);
  }
};

// ── Lifecycle ─────────────────────────────────────────────────────────────────
onMounted(() => {
  const stored = getStoredTheme() ?? (mediaQuery.matches ? 'dark' : 'light');
  showActiveTheme(stored);

  document.addEventListener('click', handleClickOutside);
  mediaQuery.addEventListener('change', onSystemChange);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  mediaQuery.removeEventListener('change', onSystemChange);
});
</script>

<style lang="scss" scoped>
.color-modes {
  .dropdown-menu {
    display: none;
    padding: 0.25rem;

    &.show { display: block; }

    li + li { margin-top: 0.125rem; }
  }

  .active { font-weight: 600; }
}
</style>